import { css } from 'aphrodite';
import { useRef, useMemo, useState } from 'react';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { bs } from '../../../styles/styles';
import { FolderIcon } from '../../svgs/icons/FolderIcon';
import {
	extractFolderName,
	extractTypedFolderName,
	isTypingFolderKeyword,
} from '../../../containers/Campaigns/folderUtils';

interface Props {
	keywords: string[];
	selectedKeywords: string[];
	disabled: boolean;
	onSelect: (keyword: string) => void;
	onClose?: () => void;
}

export function KeywordCombobox({ keywords, selectedKeywords, disabled, onSelect, onClose }: Props) {
	const [inputValue, setInputValue] = useState('');

	const trimmedInput = inputValue.trim();
	/**
	 * Tracks whether a selection was already handled via onPointerUp.
	 * In test environments (using userEvent), calling event.preventDefault() on pointerdown
	 * prevents mousedown/click from firing, so we use onPointerUp as the primary selection trigger.
	 * In real browsers, click also fires after pointerup; this flag prevents double-calling onSelect.
	 */
	const selectionFiredRef = useRef(false);

	const selectedSet = useMemo(() => new Set(selectedKeywords), [selectedKeywords]);

	const filteredKeywords = useMemo(() => {
		if (!keywords) return [];
		return keywords.filter(k => {
			if (selectedSet.has(k)) return false;
			if (!trimmedInput) return true;
			return k.toLowerCase().includes(trimmedInput.toLowerCase());
		});
	}, [keywords, selectedSet, trimmedInput]);

	const isTypingFolder = isTypingFolderKeyword(trimmedInput);
	const typedFolderName = extractTypedFolderName(trimmedInput);
	const exactMatch =
		filteredKeywords.some(k => k.toLowerCase() === trimmedInput.toLowerCase()) ||
		selectedKeywords.some(k => k.toLowerCase() === trimmedInput.toLowerCase());
	const canAddNew = isTypingFolder ? !!typedFolderName && !exactMatch : !!trimmedInput && !exactMatch;
	const newKeywordValue = isTypingFolder && typedFolderName ? `folder: ${typedFolderName}` : trimmedInput;

	const allItems = useMemo(
		() => (canAddNew ? [...filteredKeywords, newKeywordValue] : filteredKeywords),
		[filteredKeywords, canAddNew, newKeywordValue]
	);

	/**
	 * Primary selection handler — called from onPointerUp on each item.
	 * In test environments, pointerdown's preventDefault() blocks userEvent from dispatching
	 * mousedown/click, so onPointerUp is the only reliable trigger.
	 * In real browsers, click also fires (and triggers onValueChange), so we mark the
	 * selection as fired to prevent double-calling onSelect.
	 */
	const handleKeywordSelect = (keyword: string) => {
		selectionFiredRef.current = true;
		onSelect(keyword);
		setInputValue('');
		// Reset flag after the click event cycle completes in real browsers
		setTimeout(() => {
			selectionFiredRef.current = false;
		}, 0);
	};

	/**
	 * Handles value changes from base-ui's internal click mechanism (real browsers only).
	 * onSelect was already called from onPointerUp, so this only handles cleanup.
	 */
	const handleValueChange = (value: unknown) => {
		if (typeof value !== 'string' || !value) return;
		if (!selectionFiredRef.current) {
			// Fallback: in case onPointerUp wasn't the trigger (e.g., keyboard selection)
			onSelect(value);
		}
		setInputValue('');
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) onClose?.();
	};

	return (
		<Combobox
			items={allItems}
			value={null}
			onValueChange={handleValueChange}
			inputValue={inputValue}
			onInputValueChange={setInputValue}
			onOpenChange={handleOpenChange}
			filter={null}
			itemToStringLabel={(item: unknown) => item as string}
			isItemEqualToValue={(item: unknown, value: unknown) => item === value}
			disabled={disabled}
		>
			<ComboboxInput showTrigger={false} placeholder='Search or add keyword' autoFocus disabled={disabled} />
			<ComboboxContent>
				<ComboboxList>
					{filteredKeywords.map(keyword => {
						const folderName = extractFolderName(keyword);
						return (
							<ComboboxItem key={keyword} value={keyword} onPointerUp={() => handleKeywordSelect(keyword)}>
								{folderName ? (
									<span className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
										<FolderIcon fillColor='currentColor' />
										{folderName}
									</span>
								) : (
									keyword
								)}
							</ComboboxItem>
						);
					})}
					{canAddNew && (
						<ComboboxItem value={newKeywordValue} onPointerUp={() => handleKeywordSelect(newKeywordValue)}>
							{isTypingFolder && typedFolderName ? (
								<span className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
									<FolderIcon fillColor='currentColor' />
									{`Add Folder: ${typedFolderName}`}
								</span>
							) : (
								`Add Keyword: ${trimmedInput}`
							)}
						</ComboboxItem>
					)}
				</ComboboxList>
				{!filteredKeywords.length && !canAddNew && (
					<ComboboxEmpty>
						{trimmedInput ? `No keywords match "${trimmedInput}"` : 'Start typing to search keywords'}
					</ComboboxEmpty>
				)}
			</ComboboxContent>
		</Combobox>
	);
}
